// PropertyDrilldown.jsx
import { StoreOutlined } from "@mui/icons-material";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import LoanDrilldownAddressSection from "../../../../components/common/LoanDrilldownAddressSection";
import TitledEditableFieldsRenderer from "../../../../components/common/TitledEditableFieldsRenderer";
import Flx from "../../../../components/layout/Flx";
import TabPanel from "../../../../components/navigation/TabPanel";
import ToggleTabSwitcher from "../../../../components/navigation/ToggleTabSwitcher";
import TablePreviewCard from "../../../../components/ui/TablePreviewCard";
import { selectOptionsPropertyConditionAtOrigination } from "../../../../constants/selectOptions/selectOptionsPropertyConditionAtOrigination";
import { selectOptionsPropertyStatusAtOrigination } from "../../../../constants/selectOptions/selectOptionsPropertyStatusAtOrigination";
import { selectOptionsPropertyType } from "../../../../constants/selectOptions/selectOptionsPropertyType";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";

function PropertyDrilldown({ selectedId }) {
  const { id: loanId } = useParams();
  const navigate = useNavigate();

  const [selected, setSelected] = useState(null);
  const [formSpy, setFormSpy] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const [tab, setTab] = useState("Property Details");

  const onClose = useCallback(() => {
    navigate(`/loan/${loanId}/properties`);
  }, [loanId, navigate]);
  const memoizedSetTab = useCallback((newTab) => setTab(newTab), []);
  const subjectProperties = useMemo(
    () => loanDrilldown?.subjectProperties || [],
    [loanDrilldown]
  );

  const dispatch = useDispatch();
  const { updateSubjectProperty } = useUnderwritingHook();

  const onPropertyUpdate = ({ values, stopLoadingFn }) => {
    updateSubjectProperty({
      propertyId: selected?._id,
      loanId: loanDrilldown?._id,
      data: values,
      onSuccessFn: (response) => {
        // console.log("response", response);
        dispatch(loanDrilldownSet(response));
        stopLoadingFn();
      },
      onCompleteFn: () => {
        stopLoadingFn();
      },
    });
  };

  useEffect(() => {
    // if (isNil(selectedId)) return;
    if (isNil(selectedId)) {
      setSelected(null);
      return;
    }
    const property = subjectProperties?.find(
      (b) => b._id === selectedId || b.id === selectedId
    );
    setSelected(property);
  }, [selectedId, subjectProperties]);
  // Title for the preview card
  const cardTitle = selected?.address?.fullAddress || "Property Details";

  /**
   * 2. Property Details
   */
  const propertyDetailsGroup = useMemo(() => {
    return [
      {
        name: "propertyType",
        label: "Property Type",
        type: "select",
        options: selectOptionsPropertyType,
      },
      {
        name: "proposedPropertyType",
        label: "Proposed Property Type",
        type: "select",
        options: selectOptionsPropertyType,
      },
      {
        name: "propertyRuralIndicator",
        label: "Property Rural Indicator",
        type: "booleanToggle",
      },
      {
        name: "floodZone",
        label: "Flood Zone",
        type: "booleanToggle",
      },
      {
        name: "floodCertDate",
        label: "Flood Cert Date",
        type: "date",
      },
      {
        name: "propertyListedForSale",
        label: "Property Listed For Sale",
        type: "booleanToggle",
      },
      {
        name: "lastListedForSaleDate",
        label: "Last Listed For Sale Date",
        type: "date",
      },
      {
        name: "lastListedForSalePrice",
        label: "Last Listed For Sale Price",
        type: "dollar",
      },
    ];
  }, []);

  /**
   * 3. Valuation Info
   */
  const valuationInfoGroup = useMemo(() => {
    return [
      {
        name: "asIsValue",
        label: "As-Is Value",
        type: "dollar",
      },
      {
        name: "secondaryAsIsValue",
        label: "Secondary As-Is Value",
        type: "dollar",
      },
      {
        name: "secondaryAsIsVariance",
        label: "Secondary As-Is Variance",
        type: "percentage",
      },
      {
        name: "arv",
        label: "After Repair Value (ARV)",
        type: "dollar",
      },
      {
        name: "secondaryArv",
        label: "Secondary ARV",
        type: "dollar",
      },
      {
        name: "appraisalDate",
        label: "Appraisal Date",
        type: "date",
      },
      {
        name: "secondaryAppraisalDate",
        label: "Secondary Appraisal Date",
        type: "date",
      },
    ];
  }, []);

  /**
   * 4. Purchase Info
   */
  const purchaseInfoGroup = useMemo(() => {
    return [
      {
        name: "purchasePrice",
        label: "Purchase Price",
        type: "dollar",
      },
      {
        name: "purchaseDate",
        label: "Purchase Date",
        type: "date",
      },
      {
        name: "costBasis",
        label: "Cost Basis",
        type: "dollar",
      },
      {
        name: "additionalBasis",
        label: "Additional Basis",
        type: "dollar",
      },
      {
        name: "assignmentFees",
        label: "Assignment Fees",
        type: "dollar",
      },
      {
        name: "sellersConcession",
        label: "Seller's Concession",
        type: "dollar",
      },
    ];
  }, []);

  /**
   * 5. Construction / Rehab
   */
  const constructionInfoGroup = useMemo(() => {
    return [
      {
        name: "constructionBudget",
        label: "Construction Budget",
        type: "dollar",
      },
      {
        name: "financedBudget",
        label: "Financed Budget",
        type: "dollar",
      },
      {
        name: "financedBudgetPercent",
        label: "Financed Budget Percent",
        type: "percentage",
      },
      {
        name: "budgetIsFinal",
        label: "Budget Is Final",
        type: "booleanToggle",
      },
      {
        name: "budgetMatchesAppraisal",
        label: "Budget Matches Appraisal",
        type: "booleanToggle",
      },
      {
        name: "finalBudgetUploaded",
        label: "Final Budget Uploaded",
        type: "booleanToggle",
      },
      {
        name: "finalBudgetFeasible",
        label: "Final Budget Feasible",
        type: "booleanToggle",
      },
      // {
      //   name: "scopeOfWork",
      //   label: "Scope Of Work",
      //   type: "number",
      // },
    ];
  }, []);

  /**
   * 6. Property Condition
   */
  const propertyConditionGroup = useMemo(() => {
    return [
      {
        name: "propertyConditionAtOrigination",
        label: "Condition at Origination",
        type: "select",
        options: selectOptionsPropertyConditionAtOrigination,
      },
      {
        name: "propertyStatusAtOrigination",
        label: "Status at Origination",
        type: "selectMultiple",
        // type: "select",
        options: selectOptionsPropertyStatusAtOrigination,
      },
    ];
  }, []);

  /**
   * 7. Property Notes
   */

  /**
   * 8. Units / Square Footage
   */
  const unitsGroup = useMemo(() => {
    return [
      {
        name: "unitCount",
        label: "Unit Count",
      },
      {
        name: "vacantUnitCount",
        label: "Vacant Unit Count",
      },
      {
        name: "residentialUnitCount",
        label: "Residential Unit Count",
      },
      {
        name: "commercialUnitCount",
        label: "Commercial Unit Count",
      },
      {
        name: "residentialSqFootage",
        label: "Residential Sq. Footage",
        type: "float",
      },
      {
        name: "commercialSqFootage",
        label: "Commercial Sq. Footage",
        type: "float",
      },
      {
        name: "afterRenoSqFootage",
        label: "After Reno Sq. Footage",
        type: "float",
      },
      {
        name: "units",
        label: "Units Table (if applicable)",
        type: "stringMultiline",
      },
    ];
  }, []);

  /**
   * 9. Expenses (Annual & Monthly)
   */
  const expensesGroup = useMemo(() => {
    return [
      // Annual
      {
        name: "annualTaxes",
        label: "Annual Taxes",
        type: "dollar",
      },
      {
        name: "annualInsurance",
        label: "Annual Insurance",
        type: "dollar",
      },
      {
        name: "annualFloodInsurance",
        label: "Annual Flood Insurance",
        type: "dollar",
      },
      {
        name: "annualHoaFees",
        label: "Annual HOA Fees",
        type: "dollar",
      },
      {
        name: "annualPropertyMgmtFees",
        label: "Annual Property Mgmt Fees",
        type: "dollar",
      },
      {
        name: "annualOtherExpense",
        label: "Annual Other Expense",
        type: "dollar",
      },
      // Monthly
      {
        name: "monthlyTaxes",
        label: "Monthly Taxes",
        type: "dollar",
      },
      {
        name: "monthlyInsurance",
        label: "Monthly Insurance",
        type: "dollar",
      },
      {
        name: "monthlyFloodInsurance",
        label: "Monthly Flood Insurance",
        type: "dollar",
      },
      {
        name: "monthlyHoaFees",
        label: "Monthly HOA Fees",
        type: "dollar",
      },
      {
        name: "monthlyPropertyMgmtFees",
        label: "Monthly Property Mgmt Fees",
        type: "dollar",
      },
      {
        name: "monthlyOtherExpense",
        label: "Monthly Other Expense",
        type: "dollar",
      },
    ];
  }, []);

  // If no property selected, don't render anything
  if (isNil(selected) || isEmpty(selected)) {
    return null;
  }
  return (
    <TablePreviewCard
      title={cardTitle}
      onClose={onClose}
      formSpy={formSpy}
      quickFilter={quickFilter}
      setQuickFilter={setQuickFilter}
      setFormSpy={setFormSpy}
      searchPlaceholder="Search in property..."
      icon={<StoreOutlined className="thin" />}
    >
      <Flx column gap={1.5}>
        <LoanDrilldownAddressSection
          quickFilter={quickFilter}
          property={selected}
        />

        <ToggleTabSwitcher
          value={tab}
          variant={"underline"}
          onChange={memoizedSetTab}
          tabs={[
            "Property Details",
            "Valuation Info",
            "Purchase Info",
            "Construction/Rehab",
            "Units/Square Footage",
            "Property Condition",
            "Expenses",
            "All",
          ]}
        />
        <Flx column gap={3}>
          <PropertyTab tab="Property Details" tabValue={tab}>
            <TitledEditableFieldsRenderer
              uppercase
              title="Property Details"
              quickFilter={quickFilter}
              onUpdateFn={onPropertyUpdate}
              data={selected}
              fields={propertyDetailsGroup}
            />
          </PropertyTab>

          <PropertyTab tab="Valuation Info" tabValue={tab}>
            <TitledEditableFieldsRenderer
              uppercase
              title="Valuation Info"
              quickFilter={quickFilter}
              onUpdateFn={onPropertyUpdate}
              data={selected}
              fields={valuationInfoGroup}
            />
          </PropertyTab>
          <PropertyTab tab="Purchase Info" tabValue={tab}>
            <TitledEditableFieldsRenderer
              uppercase
              title="Purchase Info"
              quickFilter={quickFilter}
              onUpdateFn={onPropertyUpdate}
              data={selected}
              fields={purchaseInfoGroup}
            />
          </PropertyTab>
          <PropertyTab tab="Construction/Rehab" tabValue={tab}>
            <TitledEditableFieldsRenderer
              uppercase
              title="Construction/Rehab"
              quickFilter={quickFilter}
              onUpdateFn={onPropertyUpdate}
              data={selected}
              fields={constructionInfoGroup}
            />
          </PropertyTab>
          <PropertyTab tab="Units/Square Footage" tabValue={tab}>
            <TitledEditableFieldsRenderer
              uppercase
              title="Units/Square Footage"
              quickFilter={quickFilter}
              onUpdateFn={onPropertyUpdate}
              data={selected}
              fields={unitsGroup}
            />
          </PropertyTab>
          <PropertyTab tab="Property Condition" tabValue={tab}>
            <TitledEditableFieldsRenderer
              uppercase
              title="Property Condition"
              size={12}
              quickFilter={quickFilter}
              onUpdateFn={onPropertyUpdate}
              data={selected}
              fields={propertyConditionGroup}
            />
          </PropertyTab>
          <PropertyTab tab="Expenses" tabValue={tab}>
            <TitledEditableFieldsRenderer
              uppercase
              title="Expenses"
              size={12}
              quickFilter={quickFilter}
              onUpdateFn={onPropertyUpdate}
              data={selected}
              fields={expensesGroup}
            />
          </PropertyTab>
        </Flx>
      </Flx>
    </TablePreviewCard>
  );
}

const PropertyTab = ({ tab, tabValue, children }) => {
  return (
    <TabPanel value={tab} tabValue={tabValue} forceDisplay="All">
      {children}
    </TabPanel>
  );
};

export default React.memo(PropertyDrilldown);
